/* eslint-disable no-console */
import API from 'utils/api';
import getFormattedValues from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { API_SCHEME, API_HOST, API_PATH } from 'utils/env';

export const baseURI = 'nominas/nominas-plazas';

const getValuesAndConfig = (values) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const formattedValues = getFormattedValues(values, {
    formData: config.headers,
    clean: true,
  });
  return [formattedValues, config];
};

/**
 * GET Calculo De Nomina
 * @param {object} params
 * @returns {Promise<object[]>}
 */

// Export para mockear datos
export const verResumenNomina = async () => [
  {
    pk: 0,
    numero_plazas_calculadas: '5',
    numero_nomina: '007',
    fecha_de_calculo: '2023-01-15',
    fecha_inicial_nomina: '2023-01-01',
    fecha_final_nomina: '2023-01-15',
    dias_calculados: 0,
    importe_percepciones: 0,
    importe_deducciones: 0,
    importe_isr: 0.0,
    importe_isn: 0.0,
    importe_imss: 0.0,
    importe_issste: 0.0,
    justificacion: '1',
    periodo_fiscal: 1,
    estados_globales: 1,
  },
];

/**
 * POST Calculo De Nomina
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const calcularNomina = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

export const calcular = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Nominas Plazas
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getNominasPlazas = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Nomina Plaza
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getNominaPlaza = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data; // ← devuelve el objeto completo
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Nomina Plaza
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postNominaPlaza = async (values) => {
  try {
    const response = await API.post(baseURI, values);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Nomina Plaza
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const deleteNominaPlaza = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Importar Deduccion DED-02 - IMSS Trabajador (carga masiva por Excel)
 * Regresa el response completo: status 202 = proceso lanzado (activar polling)
 * @param {number} nominaId
 * @param {File} file
 * @returns {Promise<object|boolean>}
 */
export const importarDeduccionIMSS = async (nominaId, file) => {
  try {
    const formData = new FormData();
    formData.append('archivo', file);
    const response = await API.post(`nominas/nominas/${nominaId}/importar-deduccion-imss/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar Excel
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarExcel = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/nominas-descargar-excel-nomina/${id}`, {
      headers: {
        'Content-Type': 'application/ms-excel',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar PDF
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarPDF = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/nominas-descargar-pdf-nomina/${id}`, {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar PDF
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarZIP = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/nominas-descargar-zip-nomina/${id}`, {
      headers: {
        'Content-Type': 'application/zip',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar PDF Resumen de Nomina
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarResumenNomina = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/descargar-resumen-nomina/${id}`, {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar PDF Relacion de Recibos
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarRelacionRecibo = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/descargar-relacion-recibo/${id}`, {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descargar PDF Listado de Retencion De Pago
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const descargarListadoRetencionDePago = async (id) => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/descargar-listado-retencion-de-pago/${id}`, {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
